import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { RecordManagement } from "app/class/record-management";
import { queryClient } from "app/config/queryClient";
import { aggregateInheritedColumns } from "app/hooks/columnInheritance";
import { fetchColumns } from "app/hooks/useColumns";
import { fetchTables } from "app/hooks/useTables";
import { fetchTranslationResolve } from "app/hooks/useTranslationResolver";
import { isTranslationKey } from "component/global/translation/TranslatableInput";
import { ChoiceOption, CHOICE_QUERY_KEY, fetchChoiceOptions } from "modules/record/shared/hooks/useChoiceOptions";
import { getReferenceDisplayColumnName, normalizeReferenceRecord } from "modules/record/shared/hooks/useReferenceOptions";
import metadataQueryKeys from "shared/services/metadataQueryKeys";

export interface DisplayValueDeps {
    resolveTranslations: typeof fetchTranslationResolve;
    loadChoiceOptions: () => Promise<ChoiceOption[]>;
    loadTableColumns: (tableName: string) => Promise<SysColumn[]>;
    fetchReferenceRecord: (table: string, id: string, language: string) => Promise<unknown>;
}

const staleTime = 1000 * 60 * 5;

const defaultDeps: DisplayValueDeps = {
    resolveTranslations: fetchTranslationResolve,
    loadChoiceOptions: () => queryClient.fetchQuery({
        queryKey: CHOICE_QUERY_KEY,
        queryFn: fetchChoiceOptions,
        staleTime,
    }),
    loadTableColumns: async (tableName) => {
        const [tablesResponse, columnsResponse] = await Promise.all([
            queryClient.fetchQuery({ queryKey: metadataQueryKeys.tables, queryFn: fetchTables, staleTime }),
            queryClient.fetchQuery({ queryKey: metadataQueryKeys.columns, queryFn: fetchColumns, staleTime }),
        ]);
        const tables = tablesResponse.data.map((item: any) => new SysTable(item));
        const table = tables.find((candidate) => candidate.name === tableName);
        if (!table) return [];
        const columns = columnsResponse.data.map((item: any) => new SysColumn(item));
        return aggregateInheritedColumns(columns, tables).filter((column) => column.sys_table_id === table.id);
    },
    fetchReferenceRecord: (table, id, language) =>
        new RecordManagement(table).translationLanguage(language).get(id),
};

export const resolveKeyText = async (
    key: string,
    language: string,
    resolveTranslations: DisplayValueDeps["resolveTranslations"] = fetchTranslationResolve
): Promise<string> => {
    const resolved = await resolveTranslations([key], language);
    const partialApplication = resolved.meta?.partial_application as { reason?: string } | undefined;
    if (partialApplication?.reason === "language_fallback") return "";
    return typeof resolved.data?.[key] === "string" ? resolved.data[key] : "";
};

export const resolveDisplayValue = async ({
    column,
    value,
    language,
    deps,
}: {
    column?: SysColumn;
    value: unknown;
    language: string;
    deps?: Partial<DisplayValueDeps>;
}): Promise<unknown> => {
    const services = { ...defaultDeps, ...deps };

    if (column?.column_type === "TRANSLATABLE_TEXT") {
        if (value == null || value === "") return "";
        if (typeof value !== "string" || !isTranslationKey(value)) return value;
        return resolveKeyText(value, language, services.resolveTranslations);
    }

    if (column?.column_type === "CHOICE") {
        if (value == null || value === "") return "";
        const options = column.options ?? await services.loadChoiceOptions();
        const option = options.find((candidate) =>
            (column.options != null || candidate.ref_column === column.id) &&
            String(candidate.value) === String(value)
        );
        if (!option) return "";
        return isTranslationKey(option.label)
            ? resolveKeyText(option.label, language, services.resolveTranslations)
            : option.label;
    }

    if (column?.column_type === "REFERENCE") {
        if (value == null || value === "" || !column.referenced_table_name) return "";
        const tableName = column.referenced_table_name;
        const columns = await services.loadTableColumns(tableName);
        const displayColumnName = getReferenceDisplayColumnName(columns);
        let record: unknown;
        try {
            record = await services.fetchReferenceRecord(tableName, String(value), language);
        } catch (error) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 403 || status === 404) return "";
            throw error;
        }
        const normalized = normalizeReferenceRecord(record, String(value));
        const displayValue = normalized?.[displayColumnName];
        if (displayValue == null || displayValue === "") return "";
        const displayColumn = columns.find((candidate) => candidate.name === displayColumnName);
        if (displayColumn?.column_type === "TRANSLATABLE_TEXT" &&
            typeof displayValue === "string" && isTranslationKey(displayValue)) return "";
        return displayValue;
    }

    return value;
};
